import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socket, { postRequest, showMessage } from '../../config';
import { toggleModal } from '../../reducers/modalReducer';
import { updateFileAllocation } from '../../reducers/filesAllocationReducer';
import { SHOW_AI_FEATURES } from '../../env';
import './autoFill.css';

/**
 * AutoFillButton - Triggers auto-fill for a single question, section, or module
 *
 * Props:
 * - companyId: Company ID
 * - moduleType: Module type (e.g., 'repository')
 * - sectionCode: Optional section code (e.g., 'D001')
 * - questionId: Optional question ID for single question fill
 * - qNo: Optional question number
 * - scope: 'question' | 'section' | 'module'
 * - scopeName: Display name for scope (e.g., 'Investor Materials', 'Investment Readiness')
 * - onStart: Callback when auto-fill starts (for progress modal)
 * - onComplete: Callback when auto-fill completes
 * - disabled: Disable the button
 * - size: 'small' | 'medium' - Button size
 */
const AutoFillButton = ({
    companyId,
    moduleType,
    sectionCode,
    questionId,
    qNo,
    scope = 'question',
    scopeName = '',
    onStart,
    onComplete,
    disabled = false,
    size = 'small'
}) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const isAutoFilling = useRef(false);
    const fileInputRef = useRef(null);
    const { selectedCompany: company } = useSelector(state => state.company);
    const isMinimized = useSelector(state => !!state.modal.autoFillStatusBarVisible);
    const isMinimizedRef = useRef(false);
    const activePhase = useSelector(state => state.modal.autoFillStatusBarPhase);
    const isAutoFillActive = ['uploading', 'indexing', 'allocated', 'filling'].includes(activePhase);

    // Keep ref in sync so async functions always see latest value
    useEffect(() => {
        isMinimizedRef.current = isMinimized;
    }, [isMinimized]);

    // Listen for socket progress updates (for modal only - not when minimized to status bar)
    useEffect(() => {
        const handleProgress = (data) => {
            if (!isAutoFilling.current) return;
            dispatch(toggleModal({
                autoFillTotal: data.total || 0,
                autoFillCurrent: data.current || 0,
                autoFillCurrentQuestion: data.currentQuestion || '',
                autoFillCompleted: false
            }));
        };

        socket.on('autoFill:progress', handleProgress);

        return () => {
            socket.off('autoFill:progress', handleProgress);
        };
    }, [dispatch]);

    const handleClick = () => {
        if (loading || disabled) return;
        if (isAutoFillActive) {
            showMessage('Auto-fill is already running. Please wait for it to complete.');
            return;
        }

        // For single question, auto-fill immediately
        // For section/module, show confirmation asking if user wants to upload files first
        if (scope === 'question') {
            startAutoFill();
        } else {
            setShowConfirmModal(true);
        }
    };

    const handleUploadFiles = () => {
        setShowConfirmModal(false);
        // Trigger file input click to select files
        fileInputRef.current?.click();
    };

    const handleSkipUpload = () => {
        setShowConfirmModal(false);
        startAutoFill();
    };

    // Clone file helper for upload
    const cloneFile = (file) => {
        return new File([file], file.name, { type: file.type });
    };

    // Handle file selection - uploads files via Filing Assistant flow
    const handleFileSelect = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length === 0) return;


        const totalFiles = selectedFiles.length;
        let filesInfo = [];
        let relPaths = [];

        try {
            // Show upload progress modal + set status bar state for minimize
            dispatch(toggleModal({
                open: 'computerToClarence', percent: 0, fileName: `Uploading ${totalFiles} file${totalFiles > 1 ? 's' : ''}...`, totalFiles, filesUploaded: 0,
                autoFillStatusBarPhase: 'uploading',
                autoFillUploadProgress: { current: 0, total: totalFiles, fileName: '' },
                autoFillModuleName: company?.modules?.[moduleType]?.heading || moduleType || '',
            }));

            // Upload all files in a single request
            relPaths = selectedFiles.map(f => f.webkitRelativePath || '');
            const attachment = selectedFiles.map(f => cloneFile(f));

            const res = await postRequest('/company/uploadFile', {
                attachment, companyId, moduleType, totalFiles,
                sectionCode: sectionCode || '', scope, scopeName: scopeName || ''
            }, true);
            if (res?.body?.reports) filesInfo = res.body.reports;

            e.target.value = '';

            // Store auto-fill context early so it gets persisted to localStorage before any refresh
            dispatch(updateFileAllocation({
                autoFillContext: { enabled: true, companyId, moduleType, sectionCode, scope, scopeName },
                tab: moduleType
            }));

            // After upload completes, always switch to minimized status bar for indexing
            dispatch(toggleModal({ open: '', autoFillStatusBarVisible: true, autoFillStatusBarPhase: 'indexing' }));

            // Process files with AI allocation via Filing Assistant
            const uploadFilesRes = await postRequest('/company/uploadFiles', {
                files: filesInfo,
                companyId,
                tab: moduleType,
                relPaths,
                ...(scope === 'section' && sectionCode ? { sectionCode } : {})
            }, true);

            if (!uploadFilesRes?.body?.report) {
                dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error', autoFillError: 'File upload failed' }));
                showMessage('File upload failed');
                return;
            }

            const fileReport = uploadFilesRes.body.report;

            // Calculate allocation score
            const allocated = Object.keys(fileReport.importResult || {}).filter(
                key => fileReport.importResult[key]?.section?.some(element => !element.includes("DXXX") && !element.includes("SXXX"))
            ).length;
            const total = Object.keys(fileReport.importResult || {}).length;
            const currentScore = total === 0 ? 0 : Math.round(allocated / total * 100);

            // Store auto-fill context so ImportSummary can trigger auto-fill after allocation
            const autoFillContext = {
                enabled: true,
                companyId,
                moduleType,
                sectionCode,
                scope,
                scopeName,
                onStart,
                onComplete
            };

            // Update file allocation state with auto-fill context
            dispatch(updateFileAllocation({
                fileReport,
                company,
                currentScore,
                tab: moduleType,
                autoFillContext // Pass context for auto-fill after allocation
            }));

            // Always show status bar by default after allocation completes.
            // User can expand to full importSummary from the status bar.
            dispatch(toggleModal({ open: '', autoFillStatusBarVisible: true, autoFillStatusBarPhase: 'allocated' }));
            showMessage(uploadFilesRes.message || 'Files allocated - tap to review');

        } catch (error) {
            console.error('Upload error:', error);
            dispatch(toggleModal({ open: '' }));
            showMessage('Upload failed. Please try again.');
            e.target.value = '';
        }
    };

    const startAutoFill = async () => {
        setLoading(true);

        const payload = {
            companyId,
            moduleType,
            minConfidence: 0.7
        };

        if (scope === 'question' && (questionId || qNo)) {
            payload.questionId = questionId;
            payload.qNo = qNo;
            payload.maxQuestions = 1;
        } else if (scope === 'section' && sectionCode) {
            payload.sectionCode = sectionCode;
            payload.maxQuestions = 50;
        } else {
            payload.maxQuestions = 100;
        }

        // Show progress modal for section/module scope using existing modal system
        if (scope !== 'question') {
            // Only enable socket listener for section/module scope
            isAutoFilling.current = true;

            dispatch(toggleModal({
                open: 'autoFillingQuestions',
                autoFillModuleName: company?.modules?.[moduleType]?.heading || moduleType || '',
                autoFillTotal: 0, // Will be updated by socket when actual count is known
                autoFillCurrent: 0,
                autoFillCurrentQuestion: '',
                autoFillCompleted: false, // Ensure we show progress, not results
                autoFillProcessed: 0, // Reset previous results
                autoFillAnswered: 0,
                autoFillNeedsReview: 0,
                autoFillSkipped: 0,
                // Also set status bar state so minimize works
                autoFillStatusBarPhase: 'filling',
            }));
            // Also notify parent if callback provided
            onStart?.({ scope, moduleType, sectionCode, total: payload.maxQuestions });

            // Small delay to ensure modal is rendered before API call
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        try {
            const response = await postRequest('/clarence/auto-fill', payload);

            if (response?.code === 200) {
                const { answered, needsReview, skipped, results, processed } = response?.body || {};

                // Handle case where question is already answered
                if (processed === 0) {
                    if (scope !== 'question') {
                        dispatch(toggleModal({ open: '' }));
                    }
                    showMessage('Question is already answered');
                    onComplete?.(response?.body);
                    return;
                }

                if (scope === 'question') {
                    // For single question, show toast message
                    const result = results?.[0];
                    let message = '';
                    if (result?.status === 'answered') {
                        message = `Answered: ${result.answer} (${Math.round(result.confidence * 100)}% confidence)`;
                    } else if (result?.status === 'needs_review') {
                        message = `Needs review: ${result.answer} (${Math.round(result.confidence * 100)}% confidence)`;
                    } else {
                        message = result?.reason === 'no_relevant_docs' ? 'No relevant documents found' : 'Could not auto-fill';
                    }
                    showMessage(message);
                } else {
                    // For section/module, show results in modal + update status bar
                    dispatch(toggleModal({
                        open: 'autoFillingQuestions',
                        autoFillCompleted: true,
                        autoFillProcessed: processed || 0,
                        autoFillAnswered: answered || 0,
                        autoFillNeedsReview: needsReview || 0,
                        autoFillSkipped: skipped || 0,
                        autoFillStatusBarPhase: 'completed',
                    }));
                }

                onComplete?.(response?.body);
            } else {
                if (scope !== 'question') {
                    dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error', autoFillError: response?.message || 'Auto-fill failed' }));
                }
                showMessage(response?.message || 'Auto-fill failed');
            }
        } catch (error) {
            console.error('Auto-fill error:', error);
            // Close modal on error + set status bar error
            if (scope !== 'question') dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error', autoFillError: 'Auto-fill failed. Please try again.' }));
            showMessage('Auto-fill failed. Please try again.');
        } finally {
            setLoading(false);
            isAutoFilling.current = false;
        }
    };

    const buttonClass = size === 'small'
        ? 'auto-fill-btn auto-fill-btn-small'
        : 'auto-fill-btn auto-fill-btn-medium';

    const icon = (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                  fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    const getButtonText = () => {
        if (loading) return 'Auto-filling...';
        if (scope === 'question') return 'Auto-fill';
        if (scope === 'section') return 'Auto-fill Section';
        return 'Auto-fill All';
    };

    const getScopeName = () => {
        if (scopeName) return scopeName;
        if (scope === 'section') return sectionCode || 'this section';
        if (scope === 'module') return moduleType || 'this module';
        return 'this question';
    };

    if (!SHOW_AI_FEATURES) return null;

    return (
        <>
            <button
                onClick={handleClick}
                disabled={loading || disabled || isAutoFillActive}
                className={`${buttonClass} ${loading ? 'auto-fill-btn-loading' : ''} ${(disabled || isAutoFillActive) ? 'auto-fill-btn-disabled' : ''}`}
                title={isAutoFillActive ? 'Auto-fill is already running' : scope === 'question' ? 'Auto-fill this question from documents' : `Auto-fill ${scope} from documents`}
            >
                <span className="auto-fill-btn-icon">{icon}</span>
                <span className="auto-fill-btn-text">{getButtonText()}</span>
            </button>

            {/* Hidden file input for Filing Assistant flow */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            />

            {/* Confirmation Modal - Ask if user wants to upload files first */}
            {showConfirmModal && (
                <div className="auto-fill-confirm-overlay" onClick={() => setShowConfirmModal(false)}>
                    <div className="auto-fill-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="auto-fill-confirm-header">
                            <div className="auto-fill-confirm-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                                          fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <h3 className="auto-fill-confirm-title">Auto-Fill {getScopeName()}</h3>
                            <button className="auto-fill-confirm-close" onClick={() => setShowConfirmModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="auto-fill-confirm-content">
                            <p>Would you like to upload additional documents before auto-filling?</p>
                            <p className="auto-fill-confirm-hint">Uploading relevant documents can improve auto-fill accuracy.</p>
                        </div>
                        <div className="auto-fill-confirm-footer">
                            <button className="auto-fill-confirm-btn-skip" onClick={handleSkipUpload}>
                                Skip, Use Existing
                            </button>
                            <button className="auto-fill-confirm-btn-upload" onClick={handleUploadFiles}>
                                Upload Documents
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AutoFillButton;
